/** The backend hosts the app talks to, keyed by their short name. */
export const API_HOSTS = [
  'users',
  'sports',
  /** Classic SportsAPI (`sAPI` / sbs) — temporary for endpoints not yet on SportsAPI2. */
  'sportsLegacy',
  'wallet',
  'walletcore',
  'casino',
  'translate',
  'transactions',
  'customBets',
] as const;

export type ApiHost = (typeof API_HOSTS)[number];

interface HostConfig {
  /** Legacy identifier, kept so routes can be traced back to the old client. */
  legacy: string;
  envVar: keyof ImportMetaEnv;
  /**
   * How request bodies are encoded. The older hosts read form fields; the newer
   * ones read JSON. Sending the wrong one yields an empty model server-side.
   */
  encoding: 'form' | 'json';
  /**
   * The sports host identifies the brand with an `AccountId` header and has no
   * concept of a bearer token; everything else authenticates with `Authorization`.
   */
  auth: 'bearer' | 'account';
}

export const HOST_CONFIG: Record<ApiHost, HostConfig> = {
  users: { legacy: 'uAPI', envVar: 'VITE_API_USERS', encoding: 'form', auth: 'bearer' },
  sports: { legacy: 'sAPI20', envVar: 'VITE_API_SPORTS', encoding: 'json', auth: 'account' },
  sportsLegacy: {
    legacy: 'sAPI',
    envVar: 'VITE_API_SPORTS_LEGACY',
    encoding: 'form',
    auth: 'account',
  },
  wallet: { legacy: 'wAPI', envVar: 'VITE_API_WALLET', encoding: 'form', auth: 'bearer' },
  walletcore: {
    legacy: 'wAPICore',
    envVar: 'VITE_API_WALLET_CORE',
    encoding: 'json',
    auth: 'bearer',
  },
  casino: { legacy: 'cAPI', envVar: 'VITE_API_CASINO', encoding: 'json', auth: 'bearer' },
  translate: {
    legacy: 'translateAPI',
    envVar: 'VITE_API_TRANSLATE',
    encoding: 'json',
    auth: 'bearer',
  },
  transactions: {
    legacy: 'trsAPI',
    envVar: 'VITE_API_TRANSACTIONS',
    encoding: 'json',
    auth: 'bearer',
  },
  customBets: {
    legacy: 'cbAPI',
    envVar: 'VITE_API_CUSTOM_BETS',
    encoding: 'json',
    auth: 'bearer',
  },
};

const useDevProxy = import.meta.env.VITE_USE_DEV_PROXY === 'true';

/**
 * Base URL for a host. In development this is the same-origin `/_api/<host>`
 * prefix that Vite proxies, because the upstreams send no CORS headers.
 */
export function hostBaseUrl(host: ApiHost): string {
  if (useDevProxy) return `/_api/${host}`;

  // Vite's own ImportMetaEnv carries an `any` index signature, so narrow it back.
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const configured = env[HOST_CONFIG[host].envVar];
  if (!configured) {
    throw new Error(
      `Missing ${HOST_CONFIG[host].envVar} for the "${host}" API host. Copy .env.example to .env.local.`,
    );
  }
  return configured.replace(/\/+$/, '');
}

export function apiUrl(host: ApiHost, path: string): string {
  return `${hostBaseUrl(host)}/${path.replace(/^\/+/, '')}`;
}

export const feedUrl = import.meta.env.VITE_FEED_URL ?? '';

/**
 * Brand site origin for static assets (`/gapp/...` casino thumbs,
 * `/assets/images/vendors/svg/...` casino vendor logos,
 * `/assets/images/providers/svg/...` payment provider logos, etc.).
 * Prefers `VITE_BRAND_ORIGIN` (e.g. https://contra11.com).
 */
export function brandOriginUrl(): string {
  const configured = (import.meta.env.VITE_BRAND_ORIGIN as string | undefined)?.trim();
  if (configured) return configured.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}
